import {gql} from '@apollo/client';

import {assert} from 'Asserts';
import {prependAOrAn} from 'BenchlingUtil';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ContainersColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {
  CustomResolutionQueryType,
  ServerResolutionResult,
  TransferTableValidateDestinationContainerQuery,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {CustomEvalResult} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  resolveAnyStorableTypedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {assertNever} from 'util/Types';

import {
  GetDestinationContainerContentsAndSchema,
  GetDestinationContainerContentsAndSchemaVariables,
} from './__generated/GetDestinationContainerContentsAndSchema';
import {extractContainerId} from './extractContainerId';
import makeCustomResolutionResultFromTypedValue from './makeCustomResolutionResultFromTypedValue';

type DestinationContainerResolutionResult = DestinationContainerError;

interface DestinationContainerError {
  errorCode: DestinationContainerErrorCode;
}
enum DestinationContainerErrorCode {
  ARCHIVED_CONTAINER = 'ARCHIVED_CONTAINER',
  HAS_CONTENTS = 'HAS_CONTENTS',
  INACCESSIBLE = 'INACCESSIBLE',
  INVALID_SCHEMA = 'INVALID_SCHEMA',
}

function isError(
  resolutionResult: DestinationContainerResolutionResult | null
): resolutionResult is DestinationContainerError {
  // eslint-disable-next-line benchlint/no-in-operator
  return resolutionResult != null && 'errorCode' in resolutionResult;
}

const GET_DESTINATION_CONTAINER_CONTENTS_AND_SCHEMA_QUERY = gql`
  query GetDestinationContainerContentsAndSchema($containerIds: [StrictString]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        storableSchema {
          ... on StorableSchemaInterface {
            id
          }
        }
        contents {
          id
          containedItem {
            ... on RegistryItemInterface {
              id
            }
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ...HasArchiveRecordGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// To mock our GraphQL call, we need to wrap it in a method.
// This is different from most of our GraphQL tests, where we mock the Apollo cache.
// Here, mocking the cache won't work, because our fetch policy is `'no-cache'`.
export const stubbableFunctions = {
  async doQuery(containerId: string): Promise<GetDestinationContainerContentsAndSchema> {
    return await doQueryWithoutCache<
      GetDestinationContainerContentsAndSchema,
      GetDestinationContainerContentsAndSchemaVariables
    >(
      {query: GET_DESTINATION_CONTAINER_CONTENTS_AND_SCHEMA_QUERY},
      {variables: {containerIds: [containerId]}}
    );
  },
};

/**
 * Do data fetching for resolving the Destination Container column.
 *
 * If successful, return a State representing the container's contents.
 *
 * If there is an error, return the error code.
 */
export async function resolveDestinationContainer(
  containerId: string,
  containerSchemaId: string | null
): Promise<DestinationContainerResolutionResult | null> {
  const data = await stubbableFunctions.doQuery(containerId);

  const container = data.containers[0];
  if (isInaccessible(container)) {
    return {errorCode: DestinationContainerErrorCode.INACCESSIBLE};
  }
  if (container.archiveRecord != null) {
    return {errorCode: DestinationContainerErrorCode.ARCHIVED_CONTAINER};
  }
  if (containerSchemaId != null && containerSchemaId !== container.storableSchema.id) {
    return {errorCode: DestinationContainerErrorCode.INVALID_SCHEMA};
  }
  const contents = container.contents;
  if (contents.length > 0) {
    return {errorCode: DestinationContainerErrorCode.HAS_CONTENTS};
  }

  return null;
}

export const DestinationContainerResolver = {
  validateDestinationContainer: async (
    query: TransferTableValidateDestinationContainerQuery
  ): Promise<ServerResolutionResult> => {
    const resolutionResult = await resolveDestinationContainer(query.containerId, query.containerSchemaId);
    if (isError(resolutionResult)) {
      switch (resolutionResult.errorCode) {
        case DestinationContainerErrorCode.INACCESSIBLE:
          return makeCustomResolutionResultFromTypedValue(null);
        case DestinationContainerErrorCode.INVALID_SCHEMA:
          assert(
            query.containerSchemaName != null,
            'containerSchemaName and containerSchemaId should not be null if this error code is returned'
          );
          return {
            type: undefined,
            error: `Container must be ${prependAOrAn(query.containerSchemaName)}`,
            resolvedValue: undefined,
          };
        case DestinationContainerErrorCode.HAS_CONTENTS:
          return {
            type: undefined,
            error: 'Container is already filled',
            resolvedValue: undefined,
          };
        case DestinationContainerErrorCode.ARCHIVED_CONTAINER:
          return {
            type: undefined,
            error: 'Cannot transfer to archived container',
            resolvedValue: undefined,
          };
        default:
          throw new Error(`Unexpected error ${assertNever(resolutionResult.errorCode)}`);
      }
    }
    return makeCustomResolutionResultFromTypedValue(null);
  },
};

export const BARCODE_IN_USE_ERROR = 'Barcode already exists';

export function destinationCreateContainerCustomEval(
  typedValue: TypedValue,
  columnDefinition: ContainersColumnDefinition,
  cellDependencies: [],
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  const {error: resolutionError} = resolveAnyStorableTypedValue(resolveFromResolutionQuery, typedValue);

  let errorToShow;
  if (resolutionError == null) {
    errorToShow = new ExcessUtil.ValueError(BARCODE_IN_USE_ERROR);
  } else {
    errorToShow = null;
  }

  return {
    // Column value should always be a string
    val: {type: BaseTypedValueType.STRING, rawValue: formatTypedValue(typedValue)},
    error: errorToShow,
    locked: false,
  };
}

export function destinationFillContainerCustomEval(
  typedValue: TypedValue,
  columnDefinition: ContainersColumnDefinition,
  cellDependencies: [],
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn,
  resolveTypedValueToType: ResolveTypedValueToTypeFn
): CustomEvalResult {
  // TODO(BNCH-15744): customEval currently runs _before_ string resolution and this particular resolution
  // would like to operate on a post-resolved value, so we manually fire it off first.
  const {resolvedValue: stringResolvedValue, error: resolutionError} = resolveTypedValueToType(
    columnDefinition,
    typedValue
  );

  if (resolutionError) {
    return {
      val: stringResolvedValue,
      error: resolutionError,
      locked: false,
    };
  }

  const {containerSchemaId, containerSchemaName} = columnDefinition;
  const containerId = extractContainerId(stringResolvedValue);
  if (containerId != null) {
    const {resolvedValue, error} = resolveFromResolutionQuery(
      stringResolvedValue,
      {
        customResolutionQueryType: CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_DESTINATION_CONTAINER,
        containerId,
        containerSchemaId: containerSchemaId ?? null,
        containerSchemaName: containerSchemaName ?? null,
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {
      val: resolvedValue,
      error,
      locked: false,
    };
  }

  // TODO(BNCH-15744): the evaluator currently runs customEval before resolveTypedValue,
  // so by returning the original typed value here, we're falling back to the default validation logic
  // in resolveTypedValue
  return {
    val: typedValue,
    error: null,
    locked: false,
  };
}
